/**
 * "Add milestone" on the plan's milestones page (Roadmap M-2).
 *
 * Drawn only for a person who may edit the cycle (`process.edit`, the person-side
 * counterpart of the API's `cycles:write` scope) — nobody else sees a button they
 * could not use (§2.8). A person who may not gets one plain line saying what it takes.
 * The write is `addPlanMilestone`, the same insert the public API route makes.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal, Field, TextInput, TextArea } from '../ds';
import { addPlanMilestone } from '../../lib/planningService';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { Milestone } from '../../types/planning';

export function AddMilestoneControl({
  planVersionId,
  onAdded,
}: {
  planVersionId: string;
  onAdded: (milestone: Milestone) => void;
}) {
  const mayAdd = useUserStore((s) => s.can('process.edit'));
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [criteria, setCriteria] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mayAdd) {
    return (
      <p className="text-xs text-[var(--text-ter)]">
        Adding a milestone takes permission to edit the cycle; a workspace admin or the cycle's
        owner holds it.
      </p>
    );
  }

  const reset = () => {
    setName('');
    setTargetDate('');
    setCriteria('');
    setError(null);
  };
  const close = () => {
    if (saving) return;
    setOpen(false);
    reset();
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const { data, error: err } = await addPlanMilestone(planVersionId, {
      name,
      target_date: targetDate,
      success_criteria: criteria,
    });
    setSaving(false);
    if (err || !data) {
      setError(err ?? 'The milestone was not saved');
      return;
    }
    onAdded(data);
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
        <span className="inline-flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> Add milestone
        </span>
      </Button>
      <Modal
        open={open}
        onClose={close}
        title="Add milestone"
        description="A schedule anchor for this plan version."
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={saving || name.trim() === '' || targetDate === ''}
            >
              {saving ? 'Adding…' : 'Add'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Target date" required>
            <TextInput type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </Field>
          <Field label="Success criteria" hint="Optional. How you will know it was met.">
            <TextArea rows={3} value={criteria} onChange={(e) => setCriteria(e.target.value)} />
          </Field>
          {error && (
            <p role="alert" className="text-sm text-[var(--coral)]">
              {error}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}
